import * as React from 'react';
import { inject, deps, observer, ComponentProps } from '../../../lib/component';
import { classes, cssRaw, style } from 'typestyle';
import { TrackSchema } from '../../../types/schema';
import { TrackLine, TrackLineProps } from '../molecules/TrackLine';
import isEmpty from 'lodash/isEmpty';
import { colorLightBlueGrey, colorLightGrey, colorNeutral, colorWhite, rebrand } from '../../../theme/color';
import {
  containerTracklineMD,
  containerTracklineLG,
  containerTracklineXL,
  containerUpToTracklineMD,
  containerUpToTracklineLG,
  containerUpToTracklineXL,
  mediaDesktop,
  mediaUpToDesktop,
} from '../utilities/mediaQueries';
import { Album } from '../atoms/icons/Album';
import { HideOnError } from '../atoms/HideOnError';
import { px } from 'csx';
import { scrollToTop } from '../utilities/page';
import { LoadMoreArrowButton } from '../atoms/button/LoadMoreArrow';
import ScrollToTopButton from '../atoms/button/ScrollToTopArrowButton';
import { spacing } from '../../../theme/spacing';

// The header grid layout needs to mirror the tracklist, so we need to reference the same container queries
// Container queries aren't included in typestyle, so we have to chuck this in raw CSS
// If you're not familiar with container queries, see https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_containment/Container_queries
const tracklistHeaderContainerName = 'tracklistHeaderContainer';

cssRaw(`
  ${containerUpToTracklineMD(tracklistHeaderContainerName)} {
    .tracklist-header-release {
      display: none !important;
    }
  }

  ${containerTracklineMD(tracklistHeaderContainerName)} {
    .tracklist-header {
      grid-template-columns: 56px 232px 1fr !important;
    }
  }

  ${containerUpToTracklineLG(tracklistHeaderContainerName)} {
    .tracklist-header-variations {
      display: none !important;
    }
  }

  ${containerTracklineLG(tracklistHeaderContainerName)} {
    .tracklist-header {
      grid-template-columns: 56px 232px 56px 1fr !important;
      gap: ${spacing.DEFAULT} !important;
    }

    /* Override column gap for track title */
    .tracklist-header-title {
      margin-left: -8px !important;
    }
  }

  ${containerUpToTracklineXL(tracklistHeaderContainerName)} {
    .tracklist-header-mood,
    .tracklist-header-genre,
    .tracklist-header-catalogue-type,
    .tracklist-header-duration {
      display: none !important;
    }
  }

  ${containerTracklineXL(tracklistHeaderContainerName)} {
    .tracklist-header {
      grid-template-columns: 56px 232px 56px 1fr repeat(2, 84px) 51px 68px 160px !important;
    }
  }
`);

const styles = {
  container: style({
    maxWidth: '992px',
  }),
  trackLineWrapper: style({
    display: 'grid',
    maxWidth: '992px',
    backgroundColor: colorWhite.toHexString(),
  }),
  hideDesktop: style(mediaDesktop({ display: 'none' })),
  hideMobile: style(mediaUpToDesktop({ display: 'none' })),
  tracklistHeaderContainer: style({
    containerType: 'inline-size',
    containerName: tracklistHeaderContainerName,
  }),
  tracklistHeader: style({
    display: 'grid',
    gridTemplateColumns: '56px 1fr',
    gap: px(8),
    padding: '0 4px',
    alignItems: 'center',
    maxWidth: '992px',
    backgroundColor: colorLightBlueGrey.toHexString(),
    borderBottom: `1px solid ${colorLightGrey.toString()}`,
    $nest: {
      '& p': {
        fontSize: '12px',
        fontWeight: 700,
        margin: '12px 0px',
      },
      '& svg': {
        marginLeft: '21.5px',
      },
    },
  }),
  btnContainer: style(
    mediaUpToDesktop({
      padding: '0 16px',
    }),
    {
      display: 'flex',
      gap: spacing.DEFAULT,
      marginTop: spacing.DEFAULT,
    }
  ),
  loadMore: style({
    display: 'flex',
    alignItems: 'center',
    color: rebrand.contrast[50].toString(),
    fontWeight: 400,
    fontSize: px(16),
  }),
  trackCount: style({
    fontSize: '12px',
    margin: '0 0 0 auto',
    color: colorNeutral.toHexString(),
  }),
};

type TrackLineEvents = Pick<TrackLineProps, 'onClickLink' | 'onAddTrackToPlaylist' | 'onRemoveTrackFromPlaylist'> &
  ComponentProps;

export interface ControlledTrackListProps extends Partial<TrackLineEvents> {
  tracks: Array<TrackSchema>;
  totalTracks: number;
  onLoadMore(): void;
  className?: string;
  playerIsShowing?: boolean;
}

@inject(deps)
@observer
export class ControlledTrackList extends React.Component<ControlledTrackListProps> {
  state = {
    minimumTrackCount: 0,
    isLoadingMore: false,
  };

  constructor(props) {
    super(props);
    this.state.minimumTrackCount = this.props.tracks.length;

    this.handleLoadMore = this.handleLoadMore.bind(this);
  }

  componentDidUpdate() {
    if (this.state.minimumTrackCount > this.props.tracks.length) {
      this.setState({ minimumTrackCount: this.props.tracks.length });
    }
  }

  async handleLoadMore() {
    this.setState({ isLoadingMore: true });
    await this.props.onLoadMore();
    this.setState({ isLoadingMore: false });
  }

  render() {
    const {
      tracks,
      totalTracks,
      onClickLink,
      onAddTrackToPlaylist,
      onRemoveTrackFromPlaylist,
      className,
      playerIsShowing,
      model: {
        ui: { isMobile },
      },
    } = this.props;
    const { minimumTrackCount, isLoadingMore } = this.state;

    return (
      <div className={classes(styles.container, className)}>
        {!isEmpty(tracks) && (
          <>
            <div className={styles.tracklistHeaderContainer}>
              <div className={classes(styles.tracklistHeader, 'tracklist-header')} data-test-track-list-header>
                <Album size={13} />
                <p className="tracklist-header-title" data-test-track-list-header-title>
                  Title
                </p>
                <div className="tracklist-header-variations"></div>
                <p className="tracklist-header-release" data-test-track-list-header-album>
                  Album
                </p>
                <p className="tracklist-header-genre" data-test-track-list-header-genre>
                  Genre
                </p>
                <p className="tracklist-header-mood" data-test-track-list-header-mood>
                  Mood
                </p>
                <p className="tracklist-header-duration" data-test-track-list-header-duration>
                  Duration
                </p>
                <p className="tracklist-header-catalogue-type" data-test-track-list-header-plan>
                  Catalogue
                </p>
              </div>
            </div>
            {tracks.map((track, index) => (
              <HideOnError key={index}>
                <div className={styles.trackLineWrapper}>
                  <TrackLine
                    track={track}
                    onClickLink={onClickLink}
                    onAddTrackToPlaylist={onAddTrackToPlaylist}
                    onRemoveTrackFromPlaylist={onRemoveTrackFromPlaylist}
                  />
                </div>
              </HideOnError>
            ))}

            <div className={classes(styles.btnContainer)}>
              {minimumTrackCount < totalTracks && (
                <LoadMoreArrowButton
                  hidden={tracks.length === totalTracks}
                  handleLoadMore={this.handleLoadMore}
                  loading={isLoadingMore}
                />
              )}
              <span className={styles.trackCount}>
                Showing {tracks.length} of {totalTracks}
              </span>
            </div>
          </>
        )}

        <ScrollToTopButton scrollTop={scrollToTop} playerIsShowing={playerIsShowing} isMobile={isMobile} />
      </div>
    );
  }
}
